import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../mock';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Trang chủ' },
    { path: '/about', label: 'Thương hiệu' },
    { path: '/products', label: 'Sản phẩm' }
  ];

  const isActive = (path) => location.pathname === path;
  const hotlines = (companyInfo.hotlines?.length
    ? companyInfo.hotlines
    : [companyInfo.hotline, companyInfo.secondaryHotline]
  ).filter(Boolean);
  const formatTelHref = (value = '') => `tel:${value.replace(/\s+/g, '')}`;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 max-w-screen-4xl 3xl:px-12 4xl:px-16">
        <div className="flex items-center justify-between h-20 4xl:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img 
              src={companyInfo.logo} 
              alt={companyInfo.brandName} 
              className="h-14 w-14 object-contain 4xl:h-16 4xl:w-16"
            />
            <div className="flex flex-col">
              <span className="text-xl font-bold text-cyan-600 3xl:text-2xl">{companyInfo.brandName}</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-2 3xl:gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'text-cyan-600 bg-cyan-50'
                    : 'text-slate-700 hover:text-cyan-600 hover:bg-slate-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3 3xl:gap-4">
            {hotlines.map((phone) => (
              <a key={phone} href={formatTelHref(phone)}>
                <Button className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2 text-base 3xl:text-lg">
                  <Phone className="h-4 w-4 3xl:h-5 3xl:w-5" />
                  {phone}
                </Button>
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-cyan-600 bg-cyan-50'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-2">
                {hotlines.map((phone) => (
                  <a key={phone} href={formatTelHref(phone)}>
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white gap-2">
                      <Phone className="h-4 w-4" />
                      {phone}
                    </Button>
                  </a>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
